<template>
  <div class="text-center">
    <v-menu v-model="menu" :close-on-content-click="false" bottom offset-y>
      <template v-slot:activator="{ props }">
        <v-chip :color="filteredStatus ? 'primary' : 'primary'" v-bind="props" label>
          {{ $t("common.action.status") }}
          <span v-if="filteredStatus">&nbsp;|&nbsp;</span>
          <span class="font-weight-bold"> {{ filteredStatus }}</span>
          <v-btn
            v-if="filteredStatus"
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop="reset()"
          />
        </v-chip>
      </template>

      <v-card min-width="350" class="mt-2">
        <v-list>
          <v-list-item :title="$t('page.product.filter.filterByStatus')">
            <template v-slot:append>
              <v-btn variant="text" icon="mdi-close" @click="menu = false" />
            </template>
          </v-list-item>
        </v-list>

        <v-divider />

        <v-list class="mt-1 mx-6">
          <v-select
            class="pt-3"
            v-model="active"
            :items="statusList"
            :label="$t('common.action.status')"
          />
        </v-list>

        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="
              menu = false;
              updateFilterProducts();
            "
            color="primary"
            class="mr-2"
          >
            {{ $t("page.payment.filter.applyFilter") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import i18n from "@/app/plugins/i18n";

const menu = ref(false);
import { useProductsStore } from "@/catalog/stores/products";
const productsStore = useProductsStore();
const { setFilterProductCondition } = productsStore;

const filteredStatus = computed(() => {
  const activeStatus = productsStore.filterProductCondition.active;

  if (activeStatus === true) {
    return i18n.global.t("common.action.active");
  } else if (activeStatus === false) {
    return i18n.global.t("common.action.inactive");
  } else {
    return "";
  }
});
const active = ref("");

const statusList = [i18n.global.t("common.action.active"), i18n.global.t("common.action.inactive")];

watch(
  filteredStatus,
  (value) => {
    if (!menu.value) {
      active.value = value;
    }
  },
  { immediate: true },
);

// Restore the status filter value from the store on component mount
onMounted(() => {
  const activeStatus = productsStore.filterProductCondition.active;

  if (activeStatus === true) {
    active.value = i18n.global.t("common.action.active");
  } else if (activeStatus === false) {
    active.value = i18n.global.t("common.action.inactive");
  } else {
    active.value = "";
  }
});

const reset = async () => {
  active.value = i18n.global.t("common.action.active");
  const filterProductCondition = productsStore.filterProductCondition;
  filterProductCondition.active = true;
  setFilterProductCondition(filterProductCondition);

  // The listLoading flag will be set by the store method
  await productsStore.searchProducts({}, false);
};

const updateFilterProducts = async () => {
  const oldCondition = productsStore.filterProductCondition;
  const newStatus = active.value.toLowerCase().replace(/\s+/g, "_");

  let activeStatus: boolean | undefined;
  if (newStatus === "active") {
    activeStatus = true;
  } else if (newStatus === "inactive") {
    activeStatus = false;
  }

  const newCondition = { ...oldCondition };
  if (activeStatus === undefined) {
    delete newCondition.active;
  } else {
    newCondition.active = activeStatus;
  }
  setFilterProductCondition(newCondition);

  // The listLoading flag will be set by the store method
  await productsStore.searchProducts({}, false);
  active.value = filteredStatus.value;
};

const setStatusFilterValue = async (value: string) => {
  const filterProductCondition = useProductsStore().filterProductCondition;

  if (value) {
    filterProductCondition.active = value === "active";
  } else {
    filterProductCondition.active = true;
  }
  productsStore.setFilterProductCondition(filterProductCondition);
  await productsStore.searchProducts({}, false);
};

const setEachActive = async (value: string) => {
  active.value = value;
  await setStatusFilterValue(value);
};

const clearFilter = async () => {
  const filterProductCondition = useProductsStore().filterProductCondition;
  filterProductCondition.active = true;
  productsStore.setFilterProductCondition(filterProductCondition);
  await productsStore.searchProducts({}, false);
};
</script>
